import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, MessageCircle, Instagram, Facebook } from "lucide-react";
import { useSite } from "@/i18n/SiteContext";
import { LogoStacked } from "@/components/site/Logo";
import { waLink } from "@/lib/api";

const linkCls = "hover:text-sand-brand";
const iconBtn = "w-10 h-10 rounded-full border border-ivory/20 flex items-center justify-center hover:border-sand-brand hover:text-sand-brand";
const DESTS = ["pompei", "napoli", "ercolano", "caserta"];

export const Footer = () => {
  const { t, settings } = useSite();
  return (
    <footer className="bg-ocean text-ivory relative noise" data-testid="site-footer">
      <div className="relative z-10 max-w-6xl mx-auto px-6 py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <LogoStacked light />
          <p className="mt-6 text-[11px] tracking-[0.3em] uppercase text-sand-brand leading-relaxed" data-testid="footer-brand-tagline">{t("brand.tagline")}</p>
          <p className="mt-4 text-sm text-ivory/60 leading-relaxed max-w-xs">{t("footer.tagline")}</p>
          <div className="flex gap-3 mt-6">
            {settings.instagram && <a href={settings.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram" className={iconBtn}><Instagram size={15} /></a>}
            {settings.facebook && <a href={settings.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-facebook" className={iconBtn}><Facebook size={15} /></a>}
          </div>
        </div>
        <div className="md:col-span-3">
          <p className="text-[11px] tracking-super-wide uppercase text-sand-brand mb-5">{t("dest.eyebrow")}</p>
          <ul className="space-y-3 text-sm text-ivory/70">
            {DESTS.map((d) => <li key={d}><Link to={`/tours?destination=${d}`} className={linkCls} data-testid={`footer-dest-${d}`}>{t(`dest.${d}`)}</Link></li>)}
            <li><Link to="/packages" className={linkCls} data-testid="footer-packages">{t("nav.packages")}</Link></li>
          </ul>
        </div>
        <div className="md:col-span-2">
          <p className="text-[11px] tracking-super-wide uppercase text-sand-brand mb-5">{t("footer.company")}</p>
          <ul className="space-y-3 text-sm text-ivory/70">
            <li><Link to="/about" className={linkCls} data-testid="footer-about">{t("nav.about")}</Link></li>
            <li><Link to="/diary" className={linkCls} data-testid="footer-diary">{t("nav.diary")}</Link></li>
            <li><Link to="/contact" className={linkCls} data-testid="footer-contact">{t("nav.contact")}</Link></li>
            <li><Link to="/tours" className={linkCls} data-testid="footer-all-tours">{t("featured.viewAll")}</Link></li>
          </ul>
        </div>
        <div className="md:col-span-3">
          <p className="text-[11px] tracking-super-wide uppercase text-sand-brand mb-5">{t("footer.contact")}</p>
          <ul className="space-y-3 text-sm text-ivory/70">
            {settings.address && <li className="flex gap-2"><MapPin size={15} className="mt-0.5 text-sand-brand shrink-0" />{settings.address}</li>}
            {settings.phone && <li><a href={`tel:${settings.phone.replace(/\s/g, "")}`} className={`flex gap-2 ${linkCls}`} data-testid="footer-phone"><Phone size={15} className="mt-0.5 text-sand-brand" />{settings.phone}</a></li>}
            {settings.whatsapp && <li><a href={waLink(settings.whatsapp, t("wa.default"))} target="_blank" rel="noopener noreferrer" className={`flex gap-2 ${linkCls}`} data-testid="footer-whatsapp"><MessageCircle size={15} className="mt-0.5 text-sand-brand" />WhatsApp</a></li>}
            {settings.email && <li><a href={`mailto:${settings.email}`} className={`flex gap-2 ${linkCls}`} data-testid="footer-email"><Mail size={15} className="mt-0.5 text-sand-brand" />{settings.email}</a></li>}
          </ul>
        </div>
      </div>
      <div className="relative z-10 border-t border-ivory/10">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col md:flex-row justify-between gap-3 text-xs text-ivory/45">
          <p>© {new Date().getFullYear()} AMS Travel. {t("footer.rights")}</p>
          <p className="flex gap-4">
            <span>{t("footer.motto")}</span>
            <Link to="/privacy" className={linkCls} data-testid="footer-privacy-link">{t("legal.privacy")}</Link>
            <Link to="/admin/login" className={linkCls} data-testid="footer-admin-link">Admin</Link>
          </p>
        </div>
      </div>
    </footer>
  );
};
